import { ESensorMode, type TBounds, type TXYPoint } from '@/types';
import {
  type FloorPlan,
  type Gateway3P,
  type Room,
  type Sensor,
  type Sensor3P,
  type Spot,
  type Zone,
  Hive,
  Item,
} from '../ScopingEditor/useGetState';
import { HIVE_ICON_SIZE } from '../items/HiveItem';
import { SPOT_ICON_SIZE } from '../items/SpotItem';
import { SENSOR3P_ICON_SIZE } from '../items/Sensor3pItem';
import { GATEWAY3P_ICON_SIZE } from '../items/Gateway3pItem';
import { SensorObject } from '../sdk';
import pointInPolygon from 'point-in-polygon';
import { calculateSensorCoverage } from './sensorCoverage';

export type ItemType =
  | 'room'
  | 'sensor'
  | 'floorplan'
  | 'hive'
  | 'zone'
  | 'spot'
  | 'sensor3p'
  | 'gateway3p';

/** Converts an item id into a type as a string */
export function getTypeFromId(id: string): ItemType | null {
  if (id.toLowerCase().startsWith('room')) return 'room';
  // sensor3p must be checked before sensor — 'sensor3p_new_xxx' starts with 'sensor'
  if (id.toLowerCase().startsWith('sensor3p')) return 'sensor3p';
  if (id.toLowerCase().startsWith('sensor')) return 'sensor';
  if (id.toLowerCase().startsWith('floorplan')) return 'floorplan';
  if (id.toLowerCase().startsWith('hive')) return 'hive';
  if (id.toLowerCase().startsWith('zone')) return 'zone';
  if (id.toLowerCase().startsWith('spot')) return 'spot';
  if (id.toLowerCase().startsWith('gateway3p')) return 'gateway3p';

  console.warn('Unknown item type', id);
  return null;
}

/** Converts any type of item into rectangular coordinates. */
export function itemToCoordinates(
  item: Room | Sensor | Zone | FloorPlan | Hive | Spot | Sensor3P | Gateway3P,
  adjustedZoom?: number,
): TXYPoint[] {
  if (getTypeFromId(item.id) === 'sensor3p') {
    const sensor3pItem = item as Sensor3P;
    if (!Array.isArray(sensor3pItem.location)) return [];
    const sz = adjustedZoom ? SENSOR3P_ICON_SIZE / adjustedZoom : SENSOR3P_ICON_SIZE;
    const x = sensor3pItem.location[0] ?? 0;
    const y = sensor3pItem.location[1] ?? 0;
    return [
      [x - sz / 2, y - sz / 2],
      [x + sz / 2, y - sz / 2],
      [x + sz / 2, y + sz / 2],
      [x - sz / 2, y + sz / 2],
    ];
  }
  if (getTypeFromId(item.id) === 'gateway3p') {
    const gateway3pItem = item as Gateway3P;
    if (!Array.isArray(gateway3pItem.location)) return [];
    const sz = adjustedZoom ? GATEWAY3P_ICON_SIZE / adjustedZoom : GATEWAY3P_ICON_SIZE;
    const x = gateway3pItem.location[0] ?? 0;
    const y = gateway3pItem.location[1] ?? 0;
    return [
      [x - sz / 2, y - sz / 2],
      [x + sz / 2, y - sz / 2],
      [x + sz / 2, y + sz / 2],
      [x - sz / 2, y + sz / 2],
    ];
  }
  if (getTypeFromId(item.id) === 'spot') {
    const spotItem = item as Spot;
    if (!Array.isArray(spotItem.location)) return [];
    const sz = adjustedZoom ? SPOT_ICON_SIZE / adjustedZoom : SPOT_ICON_SIZE;
    const x = spotItem.location[0] ?? 0;
    const y = spotItem.location[1] ?? 0;
    return [
      [x - sz / 2, y - sz / 2],
      [x + sz / 2, y - sz / 2],
      [x + sz / 2, y + sz / 2],
      [x - sz / 2, y + sz / 2],
    ];
  }
  if (getTypeFromId(item.id) === 'sensor') {
    return SensorObject.fromSensor(item as Sensor).coverageCoordinates;
  }
  if (getTypeFromId(item.id) === 'hive') {
    const hiveItem = item as Hive;
    adjustedZoom = adjustedZoom || 1;
    const [x = 0, y = 0] = hiveItem.coordinates;
    return [
      [x - HIVE_ICON_SIZE / 2 / adjustedZoom, y - HIVE_ICON_SIZE / 2 / adjustedZoom],
      [x + HIVE_ICON_SIZE / 2 / adjustedZoom, y - HIVE_ICON_SIZE / 2 / adjustedZoom],
      [x + HIVE_ICON_SIZE / 2 / adjustedZoom, y + HIVE_ICON_SIZE / 2 / adjustedZoom],
      [x - HIVE_ICON_SIZE / 2 / adjustedZoom, y + HIVE_ICON_SIZE / 2 / adjustedZoom],
    ];
  }
  if (getTypeFromId(item.id) === 'floorplan') {
    const floorPlanItem = item as FloorPlan;
    return floorPlanItem.coordinates;
  }

  const roomOrZoneItem = item as Room | Zone;
  return roomOrZoneItem.coordinates;
}

export const isItemSnappable = (item: Item): boolean => {
  const itemType = getTypeFromId(item.id);
  return (
    (itemType === 'sensor' && (item as Sensor).mode !== ESensorMode.REPEATER) ||
    itemType === 'room' ||
    itemType === 'zone'
  );
};

export function doesItemContainPoint(item: Item, p: TXYPoint) {
  const coordinates = itemToCoordinates(item);
  return pointInPolygon(p, coordinates);
}

/**
 * Calculate the area of the intersection of two given rectangles.
 */
function calculateRectangleOverlap(
  [x1, y1]: TXYPoint, // top left corner of first rectangle
  [x2, y2]: TXYPoint, // bottom right corner of first rectangle
  [x3, y3]: TXYPoint, // top left corner of second rectangle
  [x4, y4]: TXYPoint, // bottom right corner of second rectangle
) {
  const rect: [TXYPoint, TXYPoint, TXYPoint, TXYPoint] = [
    [x1, y1],
    [x2, y1],
    [x2, y2],
    [x1, y2],
  ];
  if (
    !pointInPolygon([x3, y3], rect) &&
    !pointInPolygon([x4, y3], rect) &&
    !pointInPolygon([x3, y4], rect) &&
    !pointInPolygon([x3, y4], rect)
  ) {
    return 0;
  }
  // Length of intersecting part
  const w = Math.min(x2, x4) - Math.max(x1, x3);
  const h = Math.min(y2, y4) - Math.max(y1, y3);
  if (w > 0 && h > 0) {
    return w * h;
  }
  // this shouldn't happen
  return 0;
}

/**
 * Whether the item should be selected by a box selection.
 */
export function isItemBoxSelected(
  item: Item,
  [x1, y1]: TXYPoint,
  [x2, y2]: TXYPoint,
  threshold: number = 0.8,
) {
  if (getTypeFromId(item.id) === 'spot') {
    const spotItem = item as Spot;
    const [x, y] = spotItem.location;
    return x1 <= x && x <= x2 && y1 <= y && y <= y2;
  } else if (getTypeFromId(item.id) === 'hive') {
    const hiveItem = item as Hive;
    const [x, y] = hiveItem.coordinates;
    return x1 <= x && x <= x2 && y1 <= y && y <= y2;
  } else if (getTypeFromId(item.id) === 'sensor') {
    const sensorItem = item as Sensor;
    const sensorSize = calculateSensorCoverage(sensorItem) / 2;
    const [cx, cy] = sensorItem.center;
    return (
      calculateRectangleOverlap(
        [x1, y1],
        [x2, y2],
        [cx - sensorSize / 2, cy - sensorSize / 2],
        [cx + sensorSize / 2, cy + sensorSize / 2],
      ) /
        (sensorSize * sensorSize) >
      threshold
    );
  } else if (getTypeFromId(item.id) === 'sensor3p') {
    const sensor3pItem = item as Sensor3P;
    if (!Array.isArray(sensor3pItem.location)) return false;
    const [x, y] = sensor3pItem.location;
    return x1 <= x && x <= x2 && y1 <= y && y <= y2;
  } else if (getTypeFromId(item.id) === 'gateway3p') {
    const gateway3pItem = item as Gateway3P;
    if (!Array.isArray(gateway3pItem.location)) return false;
    const [x, y] = gateway3pItem.location;
    return x1 <= x && x <= x2 && y1 <= y && y <= y2;
  } else if (getTypeFromId(item.id) === 'floorplan') {
    // Floorplan is not allowed in box selection.
    return false;
  } else {
    const roomZoneItem = item as Room | Zone;
    const minX = Math.min(...roomZoneItem.coordinates.map((coor) => coor[0]));
    const maxX = Math.max(...roomZoneItem.coordinates.map((coor) => coor[0]));
    const minY = Math.min(...roomZoneItem.coordinates.map((coor) => coor[1]));
    const maxY = Math.max(...roomZoneItem.coordinates.map((coor) => coor[1]));
    return (
      calculateRectangleOverlap([x1, y1], [x2, y2], [minX, minY], [maxX, maxY]) /
        ((maxY - minY) * (maxX - minX)) >=
      threshold
    );
  }
}

export function getIconCoords(
  mount: string,
  wallMountCoverage: TBounds | null,
  ceilingMountCorner: TXYPoint | null,
  powerIconSize: number,
  rotation: number,
) {
  const topRightCorner = mount === 'wall' ? wallMountCoverage?.topRight : ceilingMountCorner;
  const adjustedSize = Math.max(powerIconSize, 0.8);

  if (!topRightCorner) return [0, 0];

  if (mount === 'ceiling') {
    return [topRightCorner[0] - adjustedSize, topRightCorner[1] - adjustedSize];
  } else if (mount === 'wall' && topRightCorner) {
    if ((rotation > 315 && rotation <= 360) || (rotation >= 0 && rotation <= 45)) {
      return [topRightCorner[0] - 0.9, topRightCorner[1] - adjustedSize];
    } else if (rotation > 45 && rotation <= 135) {
      return [topRightCorner[0] - adjustedSize, topRightCorner[1] + 0.1];
    } else if (rotation > 135 && rotation <= 225) {
      return [topRightCorner[0] + 0.2, topRightCorner[1]];
    } else if (rotation > 225 && rotation <= 315) {
      return [topRightCorner[0] + 0.1, topRightCorner[1] - adjustedSize];
    }
  }
}
